import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'components/modal';
import Progressbar from 'components/progressbar';
import Button from 'components/button';
import { customToast } from 'components/toast';
import { useTranslation } from 'react-i18next';
import {
  useGetBulkUploadStatusQuery,
} from 'redux_store/services/client/bulkUpload';
import {
  clearBulkUploadJob,
  getBulkUploadJob,
  setBulkUploadJob,
  setBulkUploadStatus,
} from 'utils/bulkUploadTracker';
import styles from '../../styles/modules/bulk-upload-progress.module.css';

const terminalStatuses = ['completed', 'failed', 'cancelled'];

const BulkUploadProgressDock = () => {
  const { t } = useTranslation(['documents', 'common']);
  const [activeJob, setActiveJob] = useState(getBulkUploadJob());
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const handleJobStarted = (event) => {
      if (event?.detail) {
        setActiveJob(event.detail);
        setBulkUploadJob(event.detail);
      }
    };
    window.addEventListener('bulk-upload-job-started', handleJobStarted);
    return () => {
      window.removeEventListener('bulk-upload-job-started', handleJobStarted);
    };
  }, []);

  const jobId = activeJob?.jobId;
  const isConsumer = Boolean(activeJob?.isConsumer);

  const { data: jobStatus } = useGetBulkUploadStatusQuery(
    { jobId, isConsumer },
    { skip: !jobId, pollingInterval: jobId ? 2000 : 0 }
  );

  useEffect(() => {
    if (jobStatus?.status) {
      setBulkUploadStatus(jobStatus);
    }
    if (jobStatus && terminalStatuses.includes(jobStatus.status)) {
      setShowResults(true);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('bulk-upload-complete', {
            detail: { job: activeJob, status: jobStatus },
          })
        );
      }
      if (jobStatus.status === 'completed') {
        customToast.success(
          t('documents:bulkUpload.toasts.processed', {
            count:
              jobStatus.files?.filter((f) => f.status === 'completed').length ||
              0,
          })
        );
      } else if (jobStatus.status === 'failed') {
        customToast.error(
          jobStatus.error_message || t('documents:bulkUpload.toasts.failed')
        );
      }
    }
  }, [jobStatus, t]);

  const progressLabel = jobStatus?.current_step
    ? jobStatus.current_step
    : t('documents:bulkUpload.progress.processing');

  const completionCounts = useMemo(() => {
    const files = jobStatus?.files || [];
    return {
      completed: files.filter((f) => f.status === 'completed').length,
      failed: files.filter((f) => f.status === 'failed').length,
      rejected: files.filter((f) => f.content_rejected).length,
    };
  }, [jobStatus]);

  const handleCloseResults = () => {
    setShowResults(false);
    clearBulkUploadJob();
    setActiveJob(null);
  };

  if (!jobId) {
    return null;
  }

  const effectiveStatus = jobStatus || {
    status: 'uploading',
    progress_percent: 0,
    current_step: t('documents:bulkUpload.progress.processing'),
    processed_files: 0,
    total_files: 0,
  };

  return (
    <>
      {!terminalStatuses.includes(effectiveStatus.status) && (
        <div className={styles.dock}>
          <div className={styles.header}>
            <span className={styles.title}>
              {t('documents:bulkUpload.title')}
            </span>
            <span className={styles.percent}>
              {effectiveStatus.progress_percent || 0}%
            </span>
          </div>
          <div className={styles.step}>
            {effectiveStatus.current_step || progressLabel}
          </div>
          <Progressbar
            value={effectiveStatus.progress_percent || 0}
            valueString={`${effectiveStatus.progress_percent || 0}%`}
            valuePosition="right"
          />
          {effectiveStatus.processed_files > 0 && (
            <div className={styles.meta}>
              {t('documents:bulkUpload.progress.processedOf', {
                processed: effectiveStatus.processed_files,
                total: effectiveStatus.total_files,
              })}
            </div>
          )}
        </div>
      )}

      {showResults && jobStatus && (
        <Modal show={showResults} width="max-w-[520px]">
          <div className={styles.modalBody}>
            <div className={styles.modalHeader}>
              <h2>{t('documents:bulkUpload.title')}</h2>
              <p>{t('documents:bulkUpload.subtitle')}</p>
            </div>
            <div className={styles.summaryRow}>
              {completionCounts.completed > 0 && (
                <span className={styles.completed}>
                  {t('documents:bulkUpload.labels.completedCount', {
                    count: completionCounts.completed,
                  })}
                </span>
              )}
              {completionCounts.failed > 0 && (
                <span className={styles.failed}>
                  {t('documents:bulkUpload.labels.failedCount', {
                    count: completionCounts.failed,
                  })}
                </span>
              )}
              {completionCounts.rejected > 0 && (
                <span className={styles.rejected}>
                  {t('documents:bulkUpload.labels.rejectedCount', {
                    count: completionCounts.rejected,
                  })}
                </span>
              )}
            </div>
            <div className={styles.actions}>
              <Button variant="primary" onClick={handleCloseResults}>
                {t('common:buttons.close')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default BulkUploadProgressDock;
